import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAdminGuard } from '../../hooks/usePermissionGuard';
import { getAllUsers, getInstructors, updateInstructorStatus, updateUserRole, updateUserPlan, updateUserExpiryDate, deleteUser, getInstructorStatusLabel, getInstructorStatusColor } from '../../services/adminService';
import { UserWithStats, InstructorStatus } from '../../types/admin';
import { getTrialDaysRemaining } from '../../services/trialService';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { Users, Shield, AlertTriangle, CheckCircle, XCircle, Filter, ArrowLeft, Search, Calendar, Trash2 } from 'lucide-react';

export default function UsersManagement() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'instructors' | 'students'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | InstructorStatus>('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithStats | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expiryDate, setExpiryDate] = useState<string>('');

  useEffect(() => {
    // Check if user is admin
    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/not-allowed");
      return;
    }
    loadUsers();
  }, [user, navigate]);

  useEffect(() => {
    applyFilters();
  }, [users, filter, statusFilter, searchEmail]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    if (filter === 'instructors') {
      filtered = filtered.filter(u => u.role === 'instructor' || u.role === 'admin');
    } else if (filter === 'students') {
      filtered = filtered.filter(u => u.role === 'student');
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => u.instructorStatus === statusFilter);
    }

    if (searchEmail.trim()) {
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(searchEmail.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const handleUpdateStatus = async (userId: string, status: InstructorStatus) => {
    if (!user) return;

    const confirmed = window.confirm(`คุณต้องการเปลี่ยนสถานะเป็น "${getInstructorStatusLabel(status)}" ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await updateInstructorStatus(userId, status, user.uid, user.email || '');
      alert('อัปเดตสถานะสำเร็จ');
      await loadUsers();
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, role: 'student' | 'instructor' | 'admin') => {
    if (!user) return;

    const confirmed = window.confirm(`คุณต้องการเปลี่ยนบทบาทเป็น "${role}" ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await updateUserRole(userId, role, user.uid, user.email || '');
      alert('อัปเดตบทบาทสำเร็จ');
      await loadUsers();
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePlan = async (userId: string, plan: string) => {
    if (!user) return;

    const confirmed = window.confirm(`คุณต้องการเปลี่ยนแพ็กเกจเป็น "${plan}" ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await updateUserPlan(userId, plan, user.uid, user.email || '');
      alert('อัปเดตแพ็กเกจสำเร็จ');
      await loadUsers();
      
      // If updating current user's plan, refresh their session data
      if (userId === user.uid) {
        console.log('[UsersManagement] Refreshing current user data...');
        // Trigger a page reload to refresh AuthContext
        window.location.reload();
      }
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateExpiryDate = async () => {
    if (!user || !selectedUser || !expiryDate) return;

    const confirmed = window.confirm(`คุณต้องการเปลี่ยนวันหมดอายุเป็น "${new Date(expiryDate).toLocaleDateString('th-TH')}" ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await updateUserExpiryDate(selectedUser.uid, new Date(expiryDate), user.uid, user.email || '');
      alert('อัปเดตวันหมดอายุสำเร็จ');
      await loadUsers();
      setSelectedUser(null);
      setExpiryDate('');
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const setExpiryFromNow = (months: number) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    setExpiryDate(date.toISOString().split('T')[0]);
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!user) return;

    const confirmed = window.confirm(
      `⚠️ คุณแน่ใจหรือไม่ที่จะลบผู้ใช้นี้?\n\n` +
      `Email: ${userEmail}\n` +
      `UID: ${userId}\n\n` +
      `การลบจะรวมถึง:\n` +
      `- บัญชีผู้ใช้\n` +
      `- คอร์สทั้งหมดที่สร้าง\n` +
      `- หน้าขายทั้งหมด\n` +
      `- ข้อมูลการชำระเงิน\n` +
      `- ข้อมูลทั้งหมดที่เกี่ยวข้อง\n\n` +
      `⚠️ การกระทำนี้ไม่สามารถย้อนกลับได้!`
    );
    
    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      `กรุณายืนยันอีกครั้ง!\n\nพิมพ์ "ยืนยัน" เพื่อลบผู้ใช้ ${userEmail}`
    );
    
    if (!doubleConfirm) return;

    setActionLoading(true);
    try {
      await deleteUser(userId, user.uid, user.email || '');
      alert(`✅ ลบผู้ใช้ ${userEmail} สำเร็จ`);
      setSelectedUser(null);
      await loadUsers();
    } catch (error: any) {
      alert(`❌ เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดข้อมูลผู้ใช้...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-textDark" />
            </button>
            <Users className="w-10 h-10 text-primary" />
            <div>
              <h1 className="text-4xl font-bold text-textDark">จัดการผู้สอน</h1>
              <p className="text-textLight">จัดการบทบาท สถานะ และแพ็กเกจของผู้สอน</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-primary">{users.length}</p>
              <p className="text-textLight text-sm">ผู้ใช้ทั้งหมด</p>
            </div>
          </Card>
          <Card>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-blue-600">
                {users.filter(u => u.role === 'instructor' || u.role === 'admin').length}
              </p>
              <p className="text-textLight text-sm">ผู้สอน</p>
            </div>
          </Card>
          <Card>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-blue-600">
                {users.filter(u => u.role === 'student').length}
              </p>
              <p className="text-textLight text-sm">นักเรียน</p>
            </div>
          </Card>
          <Card>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-red-600">
                {users.filter(u => u.instructorStatus === 'suspended').length}
              </p>
              <p className="text-textLight text-sm">ถูกระงับ</p>
            </div>
          </Card>
        </div>

        <Card>
          <div className="p-6">
            <div className="space-y-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textLight" />
                <input
                  type="text"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder="ค้นหาด้วย Email หรือชื่อผู้ใช้..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-textLight" />
                  <span className="text-sm font-semibold text-textDark">กรองตาม:</span>
                </div>
                
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">ทั้งหมด</option>
                  <option value="instructors">ผู้สอน</option>
                  <option value="students">นักเรียน</option>
                </select>

                {filter === 'instructors' && (
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="all">สถานะทั้งหมด</option>
                    <option value="active">ใช้งานปกติ</option>
                    <option value="limited">จำกัดสิทธิ์</option>
                    <option value="suspended">ระงับการใช้งาน</option>
                  </select>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">ผู้ใช้</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">บทบาท</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">แพ็กเกจ</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">วันหมดอายุ</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">สถานะ</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-textDark">Trial</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-textDark">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((u) => {
                    const trialDays = u.trialStartedAt ? getTrialDaysRemaining(u.trialStartedAt) : 0;
                    
                    return (
                      <tr key={u.uid} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold text-textDark">{u.name || 'ไม่ระบุชื่อ'}</p>
                            <p className="text-sm text-textLight">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            u.role === 'admin' ? 'bg-red-100 text-red-700' :
                            u.role === 'instructor' ? 'bg-blue-100 text-blue-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {u.role === 'admin' ? 'ผู้ดูแล' : u.role === 'instructor' ? 'ผู้สอน' : 'นักเรียน'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-textDark uppercase">
                            {u.subscriptionTier || u.plan || 'free'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {u.subscriptionEndDate ? (
                            <span className="text-sm text-textDark">
                              {u.subscriptionEndDate.toDate().toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          ) : (u.subscriptionTier === 'free' || u.plan === 'free') ? (
                            <span className="text-sm text-gray-600">
                              {(() => {
                                const startDate = u.trialStartedAt || u.createdAt;
                                if (!startDate) return '-';
                                const expiryDate = new Date(startDate.toDate().getTime() + 14 * 24 * 60 * 60 * 1000);
                                return expiryDate.toLocaleDateString('th-TH', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }) + ' (Trial)';
                              })()}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {u.role === 'instructor' && (
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getInstructorStatusColor(u.instructorStatus || 'active')}`}>
                              {getInstructorStatusLabel(u.instructorStatus || 'active')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(u.subscriptionTier === 'free' || u.plan === 'free') && u.trialStartedAt && (
                            <span className={`text-sm font-semibold ${
                              u.trialExpired ? 'text-red-600' : trialDays <= 7 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {u.trialExpired ? 'หมดอายุ' : `${trialDays} วัน`}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setSelectedUser(u)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors"
                            >
                              จัดการ
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-textDark">จัดการผู้สอน</h2>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="text-textLight hover:text-textDark"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-lg font-semibold text-textDark">{selectedUser.name}</p>
                  <p className="text-textLight">{selectedUser.email}</p>
                  {selectedUser.subscriptionEndDate && (
                    <p className="text-sm text-gray-600 mt-2">
                      วันหมดอายุปัจจุบัน: {selectedUser.subscriptionEndDate.toDate().toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  )}
                </div>

                {selectedUser.role === 'instructor' && (
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-textDark mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      สถานะผู้สอน
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        variant={selectedUser.instructorStatus === 'active' ? 'primary' : 'secondary'}
                        onClick={() => handleUpdateStatus(selectedUser.uid, 'active')}
                        disabled={actionLoading}
                        className="flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        ใช้งานปกติ
                      </Button>
                      <Button
                        variant={selectedUser.instructorStatus === 'limited' ? 'primary' : 'secondary'}
                        onClick={() => handleUpdateStatus(selectedUser.uid, 'limited')}
                        disabled={actionLoading}
                        className="flex items-center justify-center gap-2"
                      >
                        <AlertTriangle className="w-4 h-4" />
                        จำกัดสิทธิ์
                      </Button>
                      <Button
                        variant={selectedUser.instructorStatus === 'suspended' ? 'primary' : 'secondary'}
                        onClick={() => handleUpdateStatus(selectedUser.uid, 'suspended')}
                        disabled={actionLoading}
                        className="flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        ระงับ
                      </Button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-bold text-textDark mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    วันหมดอายุ
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <button
                        onClick={() => setExpiryFromNow(1)}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors"
                      >
                        +1 เดือน
                      </button>
                      <button
                        onClick={() => setExpiryFromNow(6)}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold hover:bg-purple-200 transition-colors"
                      >
                        +6 เดือน
                      </button>
                      <button
                        onClick={() => setExpiryFromNow(12)}
                        className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 transition-colors"
                      >
                        +1 ปี
                      </button>
                    </div>
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none transition-colors"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Button
                      variant="primary"
                      onClick={handleUpdateExpiryDate}
                      disabled={actionLoading || !expiryDate}
                      className="w-full"
                    >
                      บันทึกวันหมดอายุ
                    </Button>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-bold text-textDark mb-3">แพ็กเกจ</h3>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      variant={(selectedUser.subscriptionTier || selectedUser.plan) === 'free' ? 'primary' : 'secondary'}
                      onClick={() => handleUpdatePlan(selectedUser.uid, 'free')}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      FREE - ทดลองใช้ฟรี 14 วัน
                    </Button>
                    <Button
                      variant={(selectedUser.subscriptionTier || selectedUser.plan) === 'pro_monthly' ? 'primary' : 'secondary'}
                      onClick={() => handleUpdatePlan(selectedUser.uid, 'pro_monthly')}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      PRO (รายเดือน) - 690 บาท/เดือน
                    </Button>
                    <Button
                      variant={(selectedUser.subscriptionTier || selectedUser.plan) === 'pro_yearly' ? 'primary' : 'secondary'}
                      onClick={() => handleUpdatePlan(selectedUser.uid, 'pro_yearly')}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      PRO (รายปี) - 6,900 บาท/ปี
                    </Button>
                    <Button
                      variant={(selectedUser.subscriptionTier || selectedUser.plan) === 'pro_plus_monthly' ? 'primary' : 'secondary'}
                      onClick={() => handleUpdatePlan(selectedUser.uid, 'pro_plus_monthly')}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      PRO+ (รายเดือน) - 1,590 บาท/เดือน
                    </Button>
                    <Button
                      variant={(selectedUser.subscriptionTier || selectedUser.plan) === 'pro_plus_yearly' ? 'primary' : 'secondary'}
                      onClick={() => handleUpdatePlan(selectedUser.uid, 'pro_plus_yearly')}
                      disabled={actionLoading}
                      className="w-full"
                    >
                      PRO+ (รายปี) - 15,900 บาท/ปี
                    </Button>
                  </div>
                </div>

                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-bold text-red-600 mb-3 flex items-center gap-2">
                    <Trash2 className="w-5 h-5" />
                    ลบผู้ใช้
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    ⚠️ การลบผู้ใช้จะลบข้อมูลทั้งหมดที่เกี่ยวข้อง รวมถึงคอร์ส หน้าขาย และข้อมูลการชำระเงิน การกระทำนี้ไม่สามารถย้อนกลับได้!
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => handleDeleteUser(selectedUser.uid, selectedUser.email)}
                    disabled={actionLoading}
                    className="w-full bg-red-100 text-red-700 hover:bg-red-200 flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    ลบผู้ใช้และข้อมูลทั้งหมด
                  </Button>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="secondary"
                    onClick={() => setSelectedUser(null)}
                  >
                    ปิด
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
